/**
 * A text's share link and the page it opens (ADR-115 Decision 10, Implementation note 4;
 * handoff §17).
 *
 * The owner's side: making the link (a new one each time, which replaces the old) and
 * turning it off. Both write the answer into the text read's cache, so the share sheet
 * and the text screen read one source.
 *
 * The reader's side: the public read, the open bank and the answer check, none of which
 * stores anything, and saving a copy, which needs a sign-in. A dead token (never there,
 * revoked, replaced) is one 404 for every route.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { SharedCheckResult, SharedText, TextDetail, TextShare } from '../types'

const textKey = (textId: string) => ['text', textId] as const
export const sharedTextKey = (token: string) => ['shared', 'text', token] as const

/** The response was the dead link's 404 (as opposed to a failure worth retrying). */
export const isDeadLink = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status === 404

function useWriteShare(textId: string) {
  const qc = useQueryClient()
  return (share: TextShare | null) => {
    qc.setQueryData<TextDetail>(textKey(textId), (old) => (old ? { ...old, share } : old))
  }
}

export function useCreateTextShare(textId: string) {
  const write = useWriteShare(textId)
  return useMutation<TextShare, Error, void>({
    mutationFn: () => api.post<TextShare>(`/api/v1/texts/${textId}/share`).then((r) => r.data),
    onSuccess: write,
  })
}

export function useRevokeTextShare(textId: string) {
  const write = useWriteShare(textId)
  return useMutation<void, Error, void>({
    mutationFn: () => api.delete(`/api/v1/texts/${textId}/share`).then(() => undefined),
    onSuccess: () => write(null),
  })
}

export function useSharedText(token: string | undefined) {
  return useQuery<SharedText>({
    queryKey: sharedTextKey(token ?? ''),
    queryFn: () => api.get<SharedText>(`/api/v1/shared/texts/${token}`).then((r) => r.data),
    enabled: !!token,
    // A dead link is an answer, not a failure: asking again changes nothing.
    retry: (count, error) => !isDeadLink(error) && count < 2,
    refetchOnWindowFocus: false,
  })
}

export function fetchSharedBank(token: string): Promise<string[]> {
  return api.get<{ forms: string[] }>(`/api/v1/shared/texts/${token}/bank`).then((r) => r.data.forms)
}

export function checkSharedAnswer(token: string, gapId: string, answer: string, wrongCount: number): Promise<SharedCheckResult> {
  return api.post<SharedCheckResult>(`/api/v1/shared/texts/${token}/check`, {
    gap_id: gapId, answer, wrong_count: Math.min(wrongCount, 1),
  }).then((r) => r.data)
}

export function useSaveSharedText(token: string) {
  const qc = useQueryClient()
  // `newDeck` makes a deck named after the text in the same transaction as the copy.
  return useMutation<{ text_id: string; created: boolean }, Error, { deckId: string | null; newDeck?: boolean }>({
    mutationFn: ({ deckId, newDeck = false }) =>
      api.post<{ text_id: string; created: boolean }>(`/api/v1/shared/texts/${token}/save`, { deck_id: deckId, new_deck: newDeck })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['texts'] })
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['deck'] })
      // The next visit has a copy to send this reader to. Dropped, not refetched: the page
      // is on its way to the copy, and a refetch landing first would redirect with the
      // "already saved" banner instead of the save's toast.
      qc.removeQueries({ queryKey: sharedTextKey(token) })
    },
  })
}
